import { useCallback, useEffect, useState } from 'react';

export type RecentHouseholdType = 'client' | 'prospect';

export type RecentHouseholdSearch = {
  readonly id: string;
  readonly name: string;
  readonly salesforceAccountId: string | null;
  readonly type: RecentHouseholdType;
  readonly subtitle?: string;
  readonly viewedAt: number;
};

// Callers supply the household identity + type; the hook stamps `viewedAt`.
export type RecentHouseholdSearchInput = Omit<RecentHouseholdSearch, 'viewedAt'>;

const MAX_RECENTS = 5;

function isRecentSearch(entry: unknown): entry is RecentHouseholdSearch {
  if (!entry || typeof entry !== 'object') return false;
  if (!('id' in entry) || !('name' in entry) || !('salesforceAccountId' in entry)) return false;
  if (!('type' in entry) || !('viewedAt' in entry)) return false;
  const { id, name, salesforceAccountId, type, viewedAt } = entry;
  const subtitleOk = !('subtitle' in entry) || typeof entry.subtitle === 'string';
  return (
    typeof id === 'string' &&
    typeof name === 'string' &&
    (typeof salesforceAccountId === 'string' || salesforceAccountId === null) &&
    (type === 'client' || type === 'prospect') &&
    subtitleOk &&
    typeof viewedAt === 'number'
  );
}

function storageKey(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `savvy:recent-household-searches:${userId}`;
}

function readRecents(userId: string | null | undefined): RecentHouseholdSearch[] {
  if (typeof window === 'undefined') return [];
  const key = storageKey(userId);
  if (!key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentSearch).slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

function writeRecents(userId: string | null | undefined, recents: RecentHouseholdSearch[]): void {
  if (typeof window === 'undefined') return;
  const key = storageKey(userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(recents.slice(0, MAX_RECENTS)));
  } catch {
    // Quota exceeded, storage disabled, or serialization failure — degrade silently.
  }
}

export type UseRecentSearchesResult = {
  readonly recents: RecentHouseholdSearch[];
  readonly addRecent: (entry: RecentHouseholdSearchInput) => void;
  readonly clearRecents: () => void;
};

export function useRecentSearches(userId: string | null | undefined): UseRecentSearchesResult {
  const [recents, setRecents] = useState<RecentHouseholdSearch[]>([]);

  useEffect(() => {
    setRecents(readRecents(userId));
  }, [userId]);

  const addRecent = useCallback(
    (entry: RecentHouseholdSearchInput) => {
      if (!userId) return;
      const stamped: RecentHouseholdSearch = { ...entry, viewedAt: Date.now() };
      setRecents((prev) => {
        const deduped = prev.filter((r) => r.id !== stamped.id);
        const next = [stamped, ...deduped].slice(0, MAX_RECENTS);
        writeRecents(userId, next);
        return next;
      });
    },
    [userId],
  );

  const clearRecents = useCallback(() => {
    if (!userId) return;
    setRecents([]);
    writeRecents(userId, []);
  }, [userId]);

  return { recents, addRecent, clearRecents };
}
